import type {
  FunnelFightersCtaProps,
  FunnelFightersEventProperties,
  FunnelFightersIdentifyTraits,
  FunnelFightersLandingPageProps,
} from './types'

const SDK_URL = 'https://funnelfighters.io/sdk/funnelfighters.js'

// Required init option. The SDK has no default _apiHost, so without this it
// posts to relative /api/v/resolve and /api/t which hit our own origin and 405.
const FF_API_HOST = 'https://funnelfighters.io'

// We don't trust the SDK's drainQueue to replay our queued init. In production
// it silently failed to invoke sdk.init, leaving _initialized=false forever and
// dropping every track call. Instead we keep our own queue, hook script.onload,
// and call sdk.init + replay queued events ourselves.
class FunnelFightersService {
  private accountKey: string | null = null
  private sdkReady = false
  private pendingCalls: Array<(sdk: NonNullable<Window['funnelfighters']>) => void> = []

  init(accountKey: string): void {
    if (this.accountKey || typeof window === 'undefined' || !accountKey) return
    this.accountKey = accountKey
    this.loadSdkScript()
  }

  identify(userId: string, traits?: FunnelFightersIdentifyTraits): void {
    if (!userId) return
    this.enqueue(sdk => sdk.identify?.(userId, traits))
  }

  lp(lpId: string, props?: FunnelFightersLandingPageProps): void {
    if (!lpId) return
    this.enqueue(sdk => sdk.lp?.(lpId, props))
  }

  cta(ctaId: string, props?: FunnelFightersCtaProps): void {
    if (!ctaId) return
    this.enqueue(sdk => sdk.cta?.(ctaId, props))
  }

  track(eventName: string, properties?: FunnelFightersEventProperties): void {
    if (!eventName) return
    this.enqueue(sdk => sdk.track?.(eventName, properties))
  }

  // If the SDK is loaded, dispatch immediately; otherwise hold the call until
  // the script's onload fires. Always swallows throws so a third-party SDK
  // failure can't escape into the React render path.
  private enqueue(fn: (sdk: NonNullable<Window['funnelfighters']>) => void): void {
    if (typeof window === 'undefined') return
    if (this.sdkReady) {
      const sdk = window.funnelfighters
      if (!sdk) return
      try {
        fn(sdk)
      } catch {
        // intentionally swallowed
      }
      return
    }
    this.pendingCalls.push(fn)
  }

  private loadSdkScript(): void {
    if (typeof document === 'undefined') return

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SDK_URL}"]`)
    if (existing) {
      // Another loader already added the tag. window.funnelfighters can only be
      // truthy here if the SDK script already executed (we no longer install a
      // stub), so use that as the readiness signal.
      if (window.funnelfighters) {
        this.onSdkLoaded()
      } else {
        existing.addEventListener('load', () => this.onSdkLoaded(), { once: true })
      }
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = SDK_URL
    script.addEventListener('load', () => this.onSdkLoaded(), { once: true })

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    } else {
      document.head.appendChild(script)
    }
  }

  private onSdkLoaded(): void {
    if (this.sdkReady) return
    this.sdkReady = true

    const sdk = typeof window !== 'undefined' ? window.funnelfighters : undefined
    if (!sdk || !this.accountKey) return

    try {
      sdk.init?.(this.accountKey, { api_host: FF_API_HOST })
    } catch {
      // intentionally swallowed
    }

    const pending = this.pendingCalls
    this.pendingCalls = []
    for (const fn of pending) {
      try {
        fn(sdk)
      } catch {
        // intentionally swallowed
      }
    }
  }
}

export const funnelFighters = new FunnelFightersService()

export const initializeFunnelFighters = (): void => {
  const accountKey = import.meta.env.VITE_FUNNEL_FIGHTERS_KEY
  if (accountKey) funnelFighters.init(accountKey)
}
